import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import autoProcessedImg from "../../../assets/userguide/reviewconsole/AutoProcessed.png";
import reviewRequiredImg from "../../../assets/userguide/reviewconsole/ReviewRequired.png";
import reviewAndSelectionsImg from "../../../assets/userguide/reviewconsole/ReviewandSelections.png";
import vendorSearchImg from "../../../assets/userguide/reviewconsole/VendorSearch.png";
import resultsSubmissionImg from "../../../assets/userguide/reviewconsole/ResultsSubmissions.png";

const GuideImage = ({ src, alt }) => (
  <img
    src={src}
    alt={alt}
    style={{
      width: "100%",
      maxWidth: 700,
      display: "block",
      margin: "0 auto 16px",
      borderRadius: 8,
      border: "1px solid #e5e7eb",
    }}
  />
);

const HELP_CONTENT_SX = {
  fontSize: "13.5px",
  color: "#374151",
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  whiteSpace: "normal",
  wordSpacing: "normal",
  "& h2": {
    color: "#1f2937",
    marginTop: "20px",
    marginBottom: "10px",
    fontSize: "14.5px",
    fontWeight: 600,
    "&:first-of-type": {
      marginTop: 0,
    },
  },
  "& ul": {
    listStyle: "none",
    paddingLeft: 0,
    marginBottom: "12px",
  },
  "& li": {
    paddingLeft: "22px",
    marginBottom: "6px",
    position: "relative",
    fontSize: "13.5px",
    lineHeight: 1.5,
    "&:before": {
      content: '"→"',
      position: "absolute",
      left: 0,
      color: "#D31710",
      fontWeight: "bold",
      fontSize: "22px",
      fontFamily: "monospace",
      top: "-8px",
    },
  },
  "& p": {
    marginBottom: "12px",
    lineHeight: 1.6,
    fontSize: "13.5px",
  },
  "& .tab-info": {
    backgroundColor: "#fef3c7",
    borderLeft: "4px solid #f59e0b",
    padding: "10px 12px",
    marginBottom: "10px",
    borderRadius: "4px",
    fontSize: "13.5px",
    lineHeight: 1.5,
  },
};



export default function UserGuideModal({ onClose, isExpeditedFlow = false }) {
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ zIndex: 9500 }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxHeight: "92vh",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.18)",
        },
      }}
    >
      <DialogTitle sx={{ px: 2.5, pt: 2.25, pb: 1.75 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "16.5px",
              fontWeight: 700,
              color: "#111827",
              letterSpacing: "-0.01em",
            }}
          >
            📋 Review Console Page
          </Typography>
          <IconButton
            aria-label="Close help dialog"
            onClick={onClose}
            size="small"
            sx={{ color: "#9ca3af", borderRadius: "4px", p: 0.5 }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: 2.5, pb: 2.5, overflow: "auto" }}>
        <Box sx={HELP_CONTENT_SX}>
          <p>
            Here you can review search results, confirm the correct HCP/HCO
            matches, request vendor searches, and submit your final selections
            for DCR and eConsents processing.
          </p>

          <h2>🗂️ Understanding the Tabs</h2>
          <p>
            This page has two main tabs: <strong>Auto Processed</strong> and{" "}
            <strong>Review Required</strong>.
          </p>
          <div className="tab-info">
            <strong>Auto Processed Tab</strong> - Records the system could
            process on its own. They are read-only and need no action from you.
            Each record shows its DCR, Subscription and Consents status. Records
            with no match are shown here too (Source: Not Found).{" "}
            <strong>
              This tab is visible only if you have access to the Expedite flow.
            </strong>
          </div>
          <GuideImage src={autoProcessedImg} alt="Auto Processed tab" />
          <div className="tab-info">
            <strong>Review Required Tab</strong> - Records that need your
            decision. They are grouped into tabs based on where a matching
            profile was found. Review each tab before submitting:
          </div>
          <GuideImage src={reviewRequiredImg} alt="Review Required tab" />
          <div className="tab-info">
            <strong>EPH Tab</strong> - Records that are found in EPH. Note: if
            even one entity in a record (e.g., HCP,HCO or Parent HCO) is found
            in EPH, the whole record appears here — the other entity may still
            come from a Vendor source.
          </div>
          <div className="tab-info">
            <strong>VENDOR Tab</strong> - Records matched with external vendor
            sources (IQVIA, OpenData, etc.)
          </div>
          <div className="tab-info">
            <strong>NOT FOUND Tab</strong> - Records with no matching profile found
            in any system. Tick them to flag them as not found so a new profile
            can be created.{" "}
            <strong>
              This tab is available only for Non-Expedite users.
            </strong>
          </div>

          {isExpeditedFlow ? (
            <>
              <h2>✅ Selecting Records</h2>
              <p>
                Nothing is selected for you in this flow. Open a record and pick
                the candidate that matches; the record is then ticked and shown
                as Reviewed.
              </p>
              <ul>
                <li>
                  Use the header checkbox to tick every record in the current
                  tab
                </li>
                <li>
                  The message under a record (for example, Creates a DCR for
                  Existing HCP with New Parent HCO) shows what will happen once
                  you submit
                </li>
                <li>
                  To follow the automatic processing (DCR, Subscription,
                  Consents), switch to the Auto Processed tab
                </li>
              </ul>
            </>
          ) : (
            <>
              <h2>⚡ Auto Selections</h2>
              <p>
                When a record has exactly one possible HCP, HCO, or Parent HCO
                match, the system automatically pre-selects it for you. These
                are called <strong>Auto-Selected Records</strong>.
              </p>
              <ul>
                <li>
                  Auto-selected records are ticked automatically — no action
                  needed from you unless you want to change the match
                </li>
                <li>
                  Records with <strong>multiple</strong> possible matches are
                  <strong> not</strong> auto-selected; you must open them and
                  choose the correct match manually
                </li>
                <li>
                  Use the dropdown next to the header checkbox to choose
                  <strong> "Select All Auto-Selected Records"</strong> or
                  <strong> "Select All Records"</strong>
                </li>
                <li>
                  <strong>"Select All Records"</strong> also selects
                  multi-match records, leaving their match choice pending
                  review
                </li>
                <li>
                  Use the <strong>"Skip Review & Select All"</strong> button to
                  automatically select All Records and send them directly to
                  the next stage
                </li>
              </ul>
            </>
          )}

          <h2>✅ Review and Selections</h2>
          <p>
            Open any record to review its potential HCP/HCO/Parent HCO matches
            side by side with your uploaded input data.
          </p>
          <ul>
            <li>Click a record row to open its detail panel</li>
            <li>
              Verify the details of the desired result on the right side panel
            </li>
            <li>
              Use the checkbox next to a record to select or deselect it
              individually
            </li>
            <li>
              You must visit/review every tab that has records at least once
              before the Submit Review button becomes available
            </li>
          </ul>
          <GuideImage
            src={reviewAndSelectionsImg}
            alt="Review and Selections"
          />

          <h2>🔎 Vendor Search for EPH Results</h2>
          <p>
            If a record in the <strong>EPH</strong> tab doesn't have a confident
            match, you can request a vendor search against external vendor
            sources (IQVIA, OpenData, etc.) directly using the vendor search
            button in the record's detail panel.
          </p>
          <ul>
            <li>Available only for countries eligible for vendor search</li>
            <li>
              Select which entity type(s) to search — HCP, HCO, and/or Parent
              HCO
            </li>
            <li>
              The system fetches the results and updates the record once the
              vendor search completes
            </li>
          </ul>
          <GuideImage src={vendorSearchImg} alt="Vendor search for EPH results" />

          <h2>💾 Save My Review</h2>
          <p>
            Use <strong>Save My Review</strong> to save your current selections
            and come back to them later. Saved records show as Reviewed when you
            return to this page.
          </p>

          <h2>🧹 Clear Review</h2>
          <p>
            Use the <strong>Clear Review</strong> button to remove selections
            and start over. Choose which tabs to clear in the dialog.
          </p>

          <h2>📤 Results Submission</h2>
          <p>
            Once you're done reviewing, click <strong>Submit Review</strong> to
            send your selected records for processing.
          </p>
          <ul>
            <li>
              Every tab that has records must be visited before Submit Review is
              enabled
            </li>
            <li>At least one record must be selected</li>
            <li>The review deadline shown at the top of the page must not have passed</li>
          </ul>
          <p>
            Review actions are available only until the date shown at the top
            of the page. After that, Save, Clear and Submit are disabled and
            the records are read-only.
          </p>
          <GuideImage src={resultsSubmissionImg} alt="Results Submission" />

          <h2>🔍 Search and Filters</h2>
          <p>
            Use <strong>Search Records</strong> to find records by name (first
            name, last name or both), Enterprise ID, city, specialty and more.
            The <strong>All / Reviewed / Pending</strong> filters narrow the
            list by review status.
          </p>

          <h2>📊 Export Data</h2>
          <p>
            Use the <strong>Export Results</strong> button to download the data
            of the current tab for offline review or record-keeping.
          </p>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
